// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
import { jsPDF } from 'jspdf';
import type { InterviewReport, SkillAssessmentReport } from '../store/interviewHostSlice';

const BRAND_COLOR = [45, 157, 120] as [number, number, number];
const TEXT_COLOR = [33, 37, 41] as [number, number, number];
const GRAY_COLOR = [108, 117, 125] as [number, number, number];

// Helper function to handle null/undefined values
const formatValue = (value: any, defaultText = 'N/A'): string => {
  if (value === null || value === undefined || value === '') {
    return defaultText;
  }
  if (typeof value === 'boolean') {
    return value ? 'Yes' : 'No';
  }
  return String(value);
};

export function generateInterviewReportPDF(report: InterviewReport): void {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = margin;

  // Header
  pdf.setFillColor(...BRAND_COLOR);
  pdf.rect(0, 0, pageWidth, 30, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Interview Report', margin, 15);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  pdf.text(date, pageWidth - margin, 15, { align: 'right' });
  pdf.text('Confidential', margin, 23);

  yPos = 40;

  // Candidate Information
  pdf.setTextColor(...TEXT_COLOR);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Candidate Information', margin, yPos);
  pdf.setLineWidth(0.5);
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
  yPos += 8;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Name:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(formatValue(report.candidate.name), margin + 25, yPos);
  yPos += 6;

  pdf.setFont('helvetica', 'bold');
  pdf.text('Email:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(formatValue(report.candidate.email), margin + 25, yPos);
  yPos += 6;

  pdf.setFont('helvetica', 'bold');
  pdf.text('Position:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(formatValue(report.job.title), margin + 25, yPos);
  yPos += 6;

  pdf.setFont('helvetica', 'bold');
  pdf.text('Duration:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  const duration = report.summary?.duration_minutes ? `${report.summary.duration_minutes} minutes` : 'N/A';
  pdf.text(duration, margin + 25, yPos);
  yPos += 10;

  // Overall Assessment
  pdf.setTextColor(...TEXT_COLOR);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Overall Assessment', margin, yPos);
  pdf.setLineWidth(0.5);
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
  yPos += 8;

  // Score
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.setTextColor(...TEXT_COLOR);
  pdf.text('Overall Score:', margin, yPos);
  pdf.setFontSize(14);
  pdf.setFont('helvetica', 'bold');
  pdf.text(`${report.overall_score}/100`, margin + 35, yPos);
  yPos += 8;

  // Recommendation
  pdf.setTextColor(...TEXT_COLOR);
  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Recommendation:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(formatValue(report.recommendation), margin + 40, yPos);
  yPos += 6;

  if (report.recommendation_reason) {
    pdf.setFontSize(9);
    pdf.setTextColor(...GRAY_COLOR);
    const reasonLines = pdf.splitTextToSize(report.recommendation_reason, pageWidth - 2 * margin);
    pdf.text(reasonLines, margin, yPos);
    yPos += reasonLines.length * 4.5 + 8;
  } else {
    yPos += 8;
  }

  // Competency Scores
  if (report.competency_scores && report.competency_scores.length > 0) {
    pdf.setTextColor(...TEXT_COLOR);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Competency Scores', margin, yPos);
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
    yPos += 8;

    report.competency_scores.forEach((comp) => {
      if (yPos > pageHeight - 30) {
        pdf.addPage();
        yPos = margin;
      }

      pdf.setTextColor(...TEXT_COLOR);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      pdf.text(formatValue(comp.competency), margin, yPos);

      // Progress bar
      const barWidth = 70;
      const barX = pageWidth - margin - barWidth - 20;
      pdf.setFillColor(235, 235, 235);
      pdf.roundedRect(barX, yPos - 3, barWidth, 4, 2, 2, 'F');
      const progress = (comp.score / comp.max_score) * barWidth;
      pdf.setFillColor(...BRAND_COLOR);
      pdf.roundedRect(barX, yPos - 3, progress, 4, 2, 2, 'F');

      // Score text
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...TEXT_COLOR);
      pdf.text(`${comp.score}/${comp.max_score}`, pageWidth - margin - 5, yPos, { align: 'right' });

      yPos += 7;
    });

    yPos += 5;
  }

  // Evidence
  if (report.evidence && report.evidence.length > 0) {
    if (yPos > pageHeight - 40) {
      pdf.addPage();
      yPos = margin;
    }

    pdf.setTextColor(...TEXT_COLOR);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Evidence-Based Assessment', margin, yPos);
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
    yPos += 8;

    report.evidence.forEach((item, index) => {
      if (yPos > pageHeight - 40) {
        pdf.addPage();
        yPos = margin;
      }

      pdf.setTextColor(...TEXT_COLOR);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'bold');
      const claimText = `${index + 1}. ${formatValue(item.claim)}`;
      const claimLines = pdf.splitTextToSize(claimText, pageWidth - 2 * margin);
      pdf.text(claimLines, margin, yPos);
      yPos += claimLines.length * 5;

      if (item.excerpt) {
        pdf.setFont('helvetica', 'italic');
        pdf.setFontSize(9);
        pdf.setTextColor(...GRAY_COLOR);
        const excerptLines = pdf.splitTextToSize(`"${item.excerpt}"`, pageWidth - 2 * margin - 5);
        pdf.text(excerptLines, margin + 3, yPos);
        yPos += excerptLines.length * 4.5;
      }

      yPos += 6;
    });

    yPos += 5;
  }

  // Integrity & Verification
  if (yPos > pageHeight - 50) {
    pdf.addPage();
    yPos = margin;
  }

  pdf.setTextColor(...TEXT_COLOR);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Integrity & Verification', margin, yPos);
  pdf.setLineWidth(0.5);
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
  yPos += 8;

  pdf.setFontSize(10);

  // Email OTP verified
  pdf.setFont('helvetica', 'bold');
  pdf.text('Email OTP verified:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(formatValue(report.integrity.otp_verified), margin + 60, yPos);
  yPos += 6;

  // Single person on camera
  pdf.setFont('helvetica', 'bold');
  pdf.text('Single person on camera:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text('N/A', margin + 60, yPos);
  yPos += 6;

  // AI-response likelihood
  pdf.setFont('helvetica', 'bold');
  pdf.text('AI-response likelihood:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text('N/A', margin + 60, yPos);
  yPos += 6;

  // Multi-device attempt
  pdf.setFont('helvetica', 'bold');
  pdf.text('Multi-device attempt:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text('N/A', margin + 60, yPos);
  yPos += 6;

  // Focus loss (tab switch)
  pdf.setFont('helvetica', 'bold');
  pdf.text('Focus loss (tab switch):', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text('N/A', margin + 60, yPos);
  yPos += 6;

  // Speaking-speed anomaly
  pdf.setFont('helvetica', 'bold');
  pdf.text('Speaking-speed anomaly:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text('N/A', margin + 60, yPos);
  yPos += 6;

  // Suspicious pause patterns
  pdf.setFont('helvetica', 'bold');
  pdf.text('Suspicious pause patterns:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text('N/A', margin + 60, yPos);
  yPos += 6;

  yPos += 5;

  // Transcript
  if (report.transcript && report.transcript.length > 0) {
    if (yPos > pageHeight - 30) {
      pdf.addPage();
      yPos = margin;
    }

    pdf.setTextColor(...TEXT_COLOR);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Interview Transcript', margin, yPos);
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
    yPos += 8;

    const transcriptLimit = 20;
    report.transcript.slice(0, transcriptLimit).forEach((item, index) => {
      if (yPos > pageHeight - 30) {
        pdf.addPage();
        yPos = margin;
      }

      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'bold');
      pdf.setTextColor(...TEXT_COLOR);
      pdf.text(formatValue(item.speaker_label), margin, yPos);

      if (item.timestamp_label) {
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...GRAY_COLOR);
        pdf.setFontSize(8);
        pdf.text(`(${item.timestamp_label})`, margin + 30, yPos);
      }
      yPos += 5;

      if (item.text) {
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(9);
        pdf.setTextColor(...TEXT_COLOR);
        const textLines = pdf.splitTextToSize(item.text, pageWidth - 2 * margin - 5);
        pdf.text(textLines, margin + 3, yPos);
        yPos += textLines.length * 4.5 + 5;
      } else {
        yPos += 3;
      }
    });

    if (report.transcript.length > transcriptLimit) {
      pdf.setFontSize(8);
      pdf.setTextColor(...GRAY_COLOR);
      pdf.setFont('helvetica', 'italic');
      pdf.text(`... and ${report.transcript.length - transcriptLimit} more entries`, margin, yPos);
    }
  }

  // Footer
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setTextColor(...GRAY_COLOR);
    pdf.setFontSize(8);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    pdf.text('Generated by Interview Agent', pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  const filename = `Interview_Report_${report.candidate.name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(filename);
}

export function generateSkillAssessmentReportPDF(report: SkillAssessmentReport): void {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();
  const margin = 15;
  let yPos = margin;

  // Header
  pdf.setFillColor(...BRAND_COLOR);
  pdf.rect(0, 0, pageWidth, 30, 'F');
  pdf.setTextColor(255, 255, 255);
  pdf.setFontSize(20);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Skill Assessment Report', margin, 15);
  pdf.setFontSize(9);
  pdf.setFont('helvetica', 'normal');
  const date = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  pdf.text(date, pageWidth - margin, 15, { align: 'right' });
  pdf.text('Confidential', margin, 23);

  yPos = 40;

  // Candidate Information
  pdf.setTextColor(...TEXT_COLOR);
  pdf.setFontSize(12);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Candidate Information', margin, yPos);
  pdf.setLineWidth(0.5);
  pdf.setDrawColor(200, 200, 200);
  pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
  yPos += 8;

  pdf.setFontSize(10);
  pdf.setFont('helvetica', 'bold');
  pdf.text('Name:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(formatValue(report.header.candidate_name), margin + 25, yPos);
  yPos += 6;

  pdf.setFont('helvetica', 'bold');
  pdf.text('Position:', margin, yPos);
  pdf.setFont('helvetica', 'normal');
  pdf.text(formatValue(report.header.role_title), margin + 25, yPos);
  yPos += 6;

  if (report.header.status) {
    pdf.setFont('helvetica', 'bold');
    pdf.text('Status:', margin, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.text(formatValue(report.header.status), margin + 25, yPos);
    yPos += 6;
  }

  yPos += 6;

  // Profile Section (if available)
  if (report.profile) {
    // Overall Assessment
    pdf.setTextColor(...TEXT_COLOR);
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Overall Assessment', margin, yPos);
    pdf.setLineWidth(0.5);
    pdf.setDrawColor(200, 200, 200);
    pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
    yPos += 8;

    pdf.setFontSize(13);
    pdf.setFont('helvetica', 'bold');
    pdf.text(formatValue(report.profile.hero_title, 'Assessment Title Not Available'), margin, yPos);
    yPos += 7;

    if (report.profile.hero_summary) {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...GRAY_COLOR);
      const summaryLines = pdf.splitTextToSize(report.profile.hero_summary, pageWidth - 2 * margin);
      pdf.text(summaryLines, margin, yPos);
      yPos += summaryLines.length * 5 + 5;
    } else {
      pdf.setFontSize(9);
      pdf.setFont('helvetica', 'normal');
      pdf.setTextColor(...GRAY_COLOR);
      pdf.text('N/A', margin, yPos);
      yPos += 7;
    }

    // Level and Rating
    pdf.setTextColor(...TEXT_COLOR);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Level:', margin, yPos);
    pdf.setFont('helvetica', 'normal');
    pdf.text(formatValue(report.profile.level_label), margin + 20, yPos);

    if (report.profile.star_rating !== undefined && report.profile.max_stars) {
      pdf.setFont('helvetica', 'bold');
      pdf.text('Rating:', margin + 80, yPos);
      pdf.setFont('helvetica', 'normal');
      pdf.text(`${report.profile.star_rating}/${report.profile.max_stars} stars`, margin + 100, yPos);
    }

    yPos += 12;

    // Key Strengths
    if (
      report.profile.key_strengths &&
      report.profile.key_strengths.bullets &&
      report.profile.key_strengths.bullets.length > 0
    ) {
      pdf.setTextColor(...TEXT_COLOR);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatValue(report.profile.key_strengths.title, 'Key Strengths'), margin, yPos);
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
      yPos += 8;

      if (report.profile.key_strengths.summary) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...GRAY_COLOR);
        const strengthSummaryLines = pdf.splitTextToSize(report.profile.key_strengths.summary, pageWidth - 2 * margin);
        pdf.text(strengthSummaryLines, margin, yPos);
        yPos += strengthSummaryLines.length * 4.5 + 5;
      } else {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...GRAY_COLOR);
        pdf.text('N/A', margin, yPos);
        yPos += 7;
      }

      report.profile.key_strengths.bullets.forEach((bullet, index) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...TEXT_COLOR);
        const bulletTitle = `${index + 1}. ${formatValue(bullet.title)}:`;
        pdf.text(bulletTitle, margin, yPos);
        yPos += 4.5;

        if (bullet.detail) {
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...GRAY_COLOR);
          const detailLines = pdf.splitTextToSize(bullet.detail, pageWidth - 2 * margin - 5);
          pdf.text(detailLines, margin + 3, yPos);
          yPos += detailLines.length * 4.5 + 4;
        } else {
          yPos += 4;
        }
      });

      yPos += 5;
    }

    // Learning Opportunities
    if (
      report.profile.learning_opportunities &&
      report.profile.learning_opportunities.bullets &&
      report.profile.learning_opportunities.bullets.length > 0
    ) {
      if (yPos > pageHeight - 40) {
        pdf.addPage();
        yPos = margin;
      }

      pdf.setTextColor(...TEXT_COLOR);
      pdf.setFontSize(12);
      pdf.setFont('helvetica', 'bold');
      pdf.text(formatValue(report.profile.learning_opportunities.title, 'Learning Opportunities'), margin, yPos);
      pdf.setLineWidth(0.5);
      pdf.setDrawColor(200, 200, 200);
      pdf.line(margin, yPos + 2, pageWidth - margin, yPos + 2);
      yPos += 8;

      if (report.profile.learning_opportunities.summary) {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...GRAY_COLOR);
        const oppSummaryLines = pdf.splitTextToSize(
          report.profile.learning_opportunities.summary,
          pageWidth - 2 * margin
        );
        pdf.text(oppSummaryLines, margin, yPos);
        yPos += oppSummaryLines.length * 4.5 + 5;
      } else {
        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'normal');
        pdf.setTextColor(...GRAY_COLOR);
        pdf.text('N/A', margin, yPos);
        yPos += 7;
      }

      report.profile.learning_opportunities.bullets.forEach((bullet, index) => {
        if (yPos > pageHeight - 30) {
          pdf.addPage();
          yPos = margin;
        }

        pdf.setFontSize(9);
        pdf.setFont('helvetica', 'bold');
        pdf.setTextColor(...TEXT_COLOR);
        const bulletTitle = `${index + 1}. ${formatValue(bullet.title)}:`;
        pdf.text(bulletTitle, margin, yPos);
        yPos += 4.5;

        if (bullet.detail) {
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(...GRAY_COLOR);
          const detailLines = pdf.splitTextToSize(bullet.detail, pageWidth - 2 * margin - 5);
          pdf.text(detailLines, margin + 3, yPos);
          yPos += detailLines.length * 4.5 + 4;
        } else {
          yPos += 4;
        }
      });
    }
  } else {
    // Show basic info if profile is not available
    pdf.setTextColor(...GRAY_COLOR);
    pdf.setFontSize(10);
    pdf.setFont('helvetica', 'italic');
    pdf.text('Detailed assessment profile is not yet available.', margin, yPos);
    yPos += 10;

    if (report.overall?.summary) {
      pdf.setTextColor(...TEXT_COLOR);
      pdf.setFontSize(10);
      pdf.setFont('helvetica', 'normal');
      const summaryLines = pdf.splitTextToSize(report.overall.summary, pageWidth - 2 * margin);
      pdf.text(summaryLines, margin, yPos);
    }
  }

  // Footer
  const totalPages = pdf.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    pdf.setPage(i);
    pdf.setTextColor(...GRAY_COLOR);
    pdf.setFontSize(8);
    pdf.text(`Page ${i} of ${totalPages}`, pageWidth / 2, pageHeight - 10, { align: 'center' });
    pdf.text('Generated by Interview Agent', pageWidth - margin, pageHeight - 10, { align: 'right' });
  }

  const filename = `Skill_Assessment_Report_${report.header.candidate_name.replace(/\s+/g, '_')}_${new Date().toISOString().split('T')[0]}.pdf`;
  pdf.save(filename);
}
